import { UserButton } from "@clerk/react";
import { Link } from "@tanstack/react-router";
import { Palette } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import type { landingPathForRole } from "@/lib/permissions";
import { BreadcrumbDisplay } from "./breadcrumb";
import { SHELL_HEADER_HEIGHT } from "./shell-layout";

/** The literal union `landingPathForRole` returns, so `Link` stays typed
 *  against the route tree and needs no cast. */
export type ShellLandingPath = ReturnType<typeof landingPathForRole>;

/** The profile menu's look switch (NO-754): shown only to flagged viewers. */
export interface ShellLookSwitch {
  label: string;
  onSelect: () => void;
}

/**
 * Clerk's profile button, with the look switch as a menu action when there is
 * one. Two branches rather than a conditional child: Clerk validates the
 * children of `UserButton.MenuItems`, so a `null` there is an error.
 */
export function ShellUserButton({
  lookSwitch,
}: {
  lookSwitch?: ShellLookSwitch;
}): ReactElement {
  if (!lookSwitch) return <UserButton />;

  return (
    <UserButton>
      <UserButton.MenuItems>
        <UserButton.Action
          label={lookSwitch.label}
          labelIcon={<Palette className="size-4" />}
          onClick={lookSwitch.onSelect}
        />
      </UserButton.MenuItems>
    </UserButton>
  );
}

/**
 * The one top chrome bar, shared by both shells.
 *
 * Both shells used to spell their own: a 48px sand-100 strip in the RD shell and
 * a 65px bordered bar in the legacy one. Navigating between them (an admin going
 * /rds -> /appointments) moved the bar 17px, swapped its background, and slid the
 * wordmark sideways as the max-width container appeared. One component, so the
 * chrome cannot move when the route changes.
 *
 * Its height lives in shell-layout.tsx beside the geometry everything below the
 * bar is positioned from, so the bar and those offsets read one source.
 */
export function ShellHeader({
  landingPath,
  leading,
  nav,
  lookSwitch,
}: {
  /** Where the wordmark goes — role-dependent, so the caller resolves it. */
  landingPath: ShellLandingPath;
  /** Chrome that belongs left of the wordmark: the RD shell's siderail toggles. */
  leading?: ReactNode;
  /**
   * Top-level links beside the profile menu, for surfaces the viewer reaches
   * but the current shell does not otherwise link to (an admin's way into
   * Appointments from the legacy chrome). Nothing page-specific belongs here.
   */
  nav?: ReactNode;
  /** A flagged viewer's way to the other look, in the profile menu. */
  lookSwitch?: ShellLookSwitch;
}): ReactElement {
  return (
    <header
      className={`shrink-0 border-b border-border ${SHELL_HEADER_HEIGHT} flex items-center`}
    >
      <div className="w-full max-w-[1344px] mx-auto px-6 flex items-center justify-between gap-3 overflow-hidden">
        <div className="flex items-center gap-3 min-w-0">
          {leading}
          <Link
            to={landingPath}
            className="shrink-0 text-lg font-semibold tracking-tight text-foreground font-sans"
          >
            AlwaysAI
          </Link>
          <div className="min-w-0 overflow-hidden">
            <BreadcrumbDisplay />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-4">
          {nav}
          <ShellUserButton lookSwitch={lookSwitch} />
        </div>
      </div>
    </header>
  );
}
